/**
 * SettingsPage.js — RC 1.29 Profile Editor.
 *
 * Real editor over the candidate-facing columns of `public.profiles`
 * that the onboarding ProfileStep already writes. Lives inside the
 * Account area (/account?tab=settings).
 *
 * Scope is intentionally strict:
 *   - Unifies with onboarding (no parallel profile system).
 *   - Uses the existing `profiles` columns — no new migration.
 *   - Exposes the primary Source CV filename as a read-only display;
 *     upload / change still happens under /documents.
 *   - No industry-preference logic (out of scope per spec).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { User, FileText, Loader2, Save, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import CompletenessMeter from '@/components/CompletenessMeter';
import DeleteAccountSection from '@/components/DeleteAccountSection';

const VISA_OPTIONS = [
  'No visa required', 'Work visa required', 'Sponsorship needed',
  'Permanent resident', 'Citizen', 'Other',
];

const EDITABLE_FIELDS = [
  'full_name',
  'headline',
  'current_job_title',
  'current_company',
  'years_experience',
  'location',
  'visa_status',
  'summary',
  // RC 1.29.B
  'primary_domain',
  'skills_json',
];

/** Normalise `null`/`undefined`/number → editor-friendly string. */
const toStr = (v) => (v == null ? '' : String(v));

/** Case-insensitive dedupe preserving the first seen casing. */
function dedupeSkills(list) {
  const seen = new Set();
  const out  = [];
  for (const raw of list || []) {
    if (typeof raw !== 'string') continue;
    const s = raw.trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** Shallow array equality (order-sensitive). */
function sameArray(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

function diffPayload(server, form) {
  const out = {};
  for (const key of EDITABLE_FIELDS) {
    if (key === 'skills_json') {
      const s = Array.isArray(server?.skills_json) ? server.skills_json : [];
      const f = Array.isArray(form?.skills_json) ? form.skills_json : [];
      if (!sameArray(s, f)) out.skills_json = f;
      continue;
    }
    const s = toStr(server?.[key] ?? '');
    const f = toStr(form?.[key] ?? '');
    if (s !== f) {
      if (key === 'years_experience') {
        out[key] = f.trim() === '' ? null : Number(f);
      } else {
        out[key] = f.trim() === '' ? null : f.trim();
      }
    }
  }
  return out;
}

export default function SettingsPage() {
  const [server, setServer]   = useState(null);   // last-known persisted row
  const [form, setForm]       = useState(null);   // dirty editor state
  const [primaryCv, setCv]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/profile/me');
      const p = r.profile || {};
      // RC 1.29.B — normalise skills to array even if the column is
      // missing (un-migrated env) or returns null.
      p.skills_json = Array.isArray(p.skills_json) ? p.skills_json : [];
      setServer(p);
      setForm({ ...p });
      setCv(r.primary_cv || null);
    } catch (err) {
      toast.error(err?.message || 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const dirtyPatch = useMemo(
    () => (server && form ? diffPayload(server, form) : {}),
    [server, form],
  );
  const isDirty = Object.keys(dirtyPatch).length > 0;

  const onSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const r = await apiFetch('/api/profile/me', {
        method: 'POST',
        body: dirtyPatch,
      });
      setServer(r.profile);
      setForm({ ...r.profile });
      toast.success('Profile saved.');
      // RC 1.29.D — let the completeness meter refresh.
      window.dispatchEvent(new Event('ndor:profile-refresh'));
    } catch (err) {
      toast.error(err?.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const onRevert = () => {
    if (!server) return;
    setForm({ ...server });
  };

  if (loading || !form) {
    return (
      <div data-testid="settings-page" className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-ndor-ink font-outfit">Settings</h2>
          <p className="text-sm text-ndor-ink-secondary mt-1 font-manrope">Loading your profile…</p>
        </div>
        <Card className="border-ndor-edge shadow-sm">
          <CardContent className="p-10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-ndor-ink font-outfit">Settings</h2>
        <p className="text-sm text-ndor-ink-secondary mt-1 font-manrope">
          Update the profile that NDOR uses to match and score roles for you.
        </p>
      </div>

      {/* RC 1.29.D — Completeness meter at the top. */}
      <CompletenessMeter />

      <Card className="border-ndor-edge shadow-sm" data-testid="profile-editor-card" id="profile-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium text-ndor-ink">
            <User className="h-5 w-5 text-ndor-ink-muted" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Full name</Label>
              <Input
                value={toStr(form.full_name)}
                onChange={set('full_name')}
                placeholder="Jane Doe"
                className="h-10"
                data-testid="profile-field-full_name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Headline</Label>
              <Input
                value={toStr(form.headline)}
                onChange={set('headline')}
                placeholder="Senior Product Designer"
                className="h-10"
                data-testid="profile-field-headline"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Current job title</Label>
              <Input
                value={toStr(form.current_job_title)}
                onChange={set('current_job_title')}
                placeholder="Product Designer"
                className="h-10"
                data-testid="profile-field-current_job_title"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Current company</Label>
              <Input
                value={toStr(form.current_company)}
                onChange={set('current_company')}
                placeholder="Acme Inc."
                className="h-10"
                data-testid="profile-field-current_company"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Years of experience</Label>
              <Input
                type="number"
                min={0}
                max={80}
                value={toStr(form.years_experience)}
                onChange={set('years_experience')}
                placeholder="5"
                className="h-10"
                data-testid="profile-field-years_experience"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Location</Label>
              <Input
                value={toStr(form.location)}
                onChange={set('location')}
                placeholder="London, UK"
                className="h-10"
                data-testid="profile-field-location"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Visa / work authorisation</Label>
            <Select
              value={toStr(form.visa_status)}
              onValueChange={(v) => setForm((f) => ({ ...f, visa_status: v }))}
            >
              <SelectTrigger className="h-10" data-testid="profile-field-visa_status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {VISA_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Experience summary</Label>
            <Textarea
              value={toStr(form.summary)}
              onChange={set('summary')}
              placeholder="A brief overview of your experience and goals…"
              rows={4}
              data-testid="profile-field-summary"
            />
            <p className="text-xs text-ndor-ink-muted">
              NDOR uses this to frame fit analysis. Keep it concise and
              specific — what you do, for whom, and what outcomes you drive.
            </p>
          </div>

          {/* RC 1.29.B — primary domain (identity, not preference).
              RC 1.30.A.1 — Chip-style single-value input. Feels like
              Skills but is capped at one value. No taxonomy, no
              dropdown, no browser suggestions. */}
          <PrimaryDomainChipField
            value={toStr(form.primary_domain)}
            onChange={(next) => setForm((f) => ({ ...f, primary_domain: next }))}
          />

          {/* RC 1.29.B — skills chip input. Backend stores as
              skills_json (jsonb array of strings). */}
          <SkillsChipField
            value={Array.isArray(form.skills_json) ? form.skills_json : []}
            onChange={(next) => setForm((f) => ({ ...f, skills_json: next }))}
          />

          {/* Source CV — read-only link to /documents for changes. */}
          <div className="space-y-1.5">
            <Label className="text-ndor-ink flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-ndor-ink-muted" />
              Primary Source CV
            </Label>
            <div
              className="flex items-center justify-between rounded-md border border-ndor-edge bg-ndor-surface-alt px-3 py-2"
              data-testid="profile-primary-cv"
            >
              <span className="text-sm text-ndor-ink truncate">
                {primaryCv?.original_filename || (
                  <span className="text-ndor-ink-muted">No primary Source CV set yet.</span>
                )}
              </span>
              <a
                href="/documents"
                className="text-xs text-ndor-gold hover:underline shrink-0 ml-2"
                data-testid="profile-primary-cv-manage-link"
              >
                Manage in Documents →
              </a>
            </div>
          </div>

          {/* Action row */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-ndor-edge">
            <Button
              variant="ghost"
              size="sm"
              onClick={onRevert}
              disabled={!isDirty || saving}
              data-testid="profile-revert-button"
            >
              Revert
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={!isDirty || saving}
              data-testid="profile-save-button"
              className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* RC 1.43.B — Preferences moved to dedicated /account?tab=preferences
          tab so the full unified preference centre has its own home. */}

      {/* RC 1.44 P0.4 — Account deletion in the Danger Zone. */}
      <DeleteAccountSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RC 1.29.B — SkillsChipField.
//
// Chip-based skill editor. Commits the current text buffer to the
// chip list on Enter, `,` (comma) or blur. Backspace on an empty
// buffer removes the last chip. Clicking `×` on a chip removes it.
// Case-insensitive dedupe with first-seen casing preserved — same
// rule the backend enforces server-side.
// ─────────────────────────────────────────────────────────────
function SkillsChipField({ value, onChange }) {
  const [buffer, setBuffer] = useState('');
  const skills = Array.isArray(value) ? value : [];

  const commitBuffer = () => {
    const raw = buffer.trim();
    if (!raw) { setBuffer(''); return; }
    const next = dedupeSkills([...skills, raw]);
    if (next.length !== skills.length) onChange(next);
    setBuffer('');
  };

  const removeAt = (idx) => {
    const next = skills.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitBuffer();
    } else if (e.key === 'Backspace' && buffer === '' && skills.length > 0) {
      removeAt(skills.length - 1);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="profile-field-skills_json">
      <Label className="text-ndor-ink">Skills</Label>
      <div
        className="flex flex-wrap gap-1.5 min-h-[2.5rem] rounded-md border border-ndor-edge bg-ndor-surface-alt px-2 py-1.5 focus-within:border-ndor-ink"
        onClick={(e) => {
          const input = e.currentTarget.querySelector('input');
          input?.focus();
        }}
      >
        {skills.map((s, idx) => (
          <Badge
            key={`${s}-${idx}`}
            variant="outline"
            className="bg-ndor-surface border-ndor-edge text-xs flex items-center gap-1 pr-1"
            data-testid={`profile-skill-chip-${idx}`}
          >
            <span className="truncate max-w-[12rem]">{s}</span>
            <button
              type="button"
              aria-label={`Remove ${s}`}
              onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
              className="text-ndor-ink-muted hover:text-ndor-error ml-0.5"
              data-testid={`profile-skill-remove-${idx}`}
            >
              <XIcon className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          type="text"
          value={buffer}
          onChange={(e) => setBuffer(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commitBuffer}
          placeholder={skills.length === 0 ? 'Add a skill and press Enter…' : ''}
          className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm text-ndor-ink placeholder:text-ndor-ink-muted"
          data-testid="profile-skill-input"
        />
      </div>
      <p className="text-xs text-ndor-ink-muted">
        Press <span className="font-mono">Enter</span> or <span className="font-mono">,</span> to add a skill. Click <span className="font-mono">×</span> to remove. {skills.length}{' '}
        skill{skills.length === 1 ? '' : 's'}.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RC 1.30.A.1 — PrimaryDomainChipField.
//
// Single-value chip input. Feels like Skills but caps the list at one
// entry. Typing + Enter/comma/blur commits the buffer into the chip;
// while a chip exists the text input is hidden (you must remove the
// chip to edit). Click the chip or the × to clear it. No taxonomy,
// no datalist, no browser autocomplete.
// ─────────────────────────────────────────────────────────────
function PrimaryDomainChipField({ value, onChange }) {
  const [buffer, setBuffer] = useState('');
  const has = typeof value === 'string' && value.trim().length > 0;

  const commit = () => {
    const raw = buffer.trim();
    if (!raw) { setBuffer(''); return; }
    onChange(raw);
    setBuffer('');
  };

  const clear = () => {
    onChange('');
    // Re-focus handled by the click handler on the wrapper.
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && buffer === '' && has) {
      clear();
    }
  };

  return (
    <div className="space-y-1.5" data-testid="profile-field-primary_domain">
      <Label className="text-ndor-ink">Primary domain</Label>
      <div
        className="flex flex-wrap gap-1.5 min-h-[2.5rem] rounded-md border border-ndor-edge bg-ndor-surface-alt px-2 py-1.5 focus-within:border-ndor-ink"
        onClick={(e) => {
          const input = e.currentTarget.querySelector('input');
          input?.focus();
        }}
      >
        {has && (
          <Badge
            variant="outline"
            className="bg-ndor-surface border-ndor-edge text-xs flex items-center gap-1 pr-1"
            data-testid="profile-primary-domain-chip"
          >
            <span className="truncate max-w-[16rem]">{value}</span>
            <button
              type="button"
              aria-label="Remove primary domain"
              onClick={(e) => { e.stopPropagation(); clear(); }}
              className="text-ndor-ink-muted hover:text-ndor-error ml-0.5"
              data-testid="profile-primary-domain-remove"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {!has && (
          <input
            type="text"
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
            placeholder="Type your domain and press Enter…"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm text-ndor-ink placeholder:text-ndor-ink-muted"
            data-testid="profile-primary-domain-input"
          />
        )}
      </div>
      <p className="text-xs text-ndor-ink-muted">
        One short label for the space you work in — e.g. <span className="font-mono">Energy</span>,{' '}
        <span className="font-mono">Product Design</span>, <span className="font-mono">Data Platform</span>.{' '}
        Press <span className="font-mono">Enter</span> or <span className="font-mono">,</span> to set.
        Click <span className="font-mono">×</span> to clear and replace.
      </p>
    </div>
  );
}

